// scheduling/components/pool/AvailableStaffPanel.jsx
// The manager's view of who is free right now (§4.1, Trello #257).
//
// Ordered LONGEST-WAITING FIRST, which is the whole point: rank 1 is the fair
// pick. Ranks are shown so that reaching past someone is a visible choice rather
// than an invisible one — the same information the §6 admin audit records.
//
// Two ways to act, matching DJB's two manager-side gestures:
//   • A pool has work  → assign a pooled job from its card (Assign on the card).
//   • Pools are empty  → "Request" someone here. That's channel 3: the manager
//     wants a person before there's a pooled job for them. An email + notification,
//     no PE write, and it clears their availability (they've been given a direction).
//
// Capacity hours shown here are the staffer's own in-the-moment signal, NOT
// anything derived from their schedule.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { POOL_META } from '../../utils/poolEligibility';
import { requestPoolStaff } from '../../services/poolApi';

function fmtWaited(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return mins % 60 ? `${h}h ${mins % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function AvailableStaffPanel({ available, loading, onRequested }) {
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [requesting, setRequesting] = useState(null);   // staff row
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const depts = useMemo(() => {
    const s = new Set((available || []).map(a => a.DepartmentCode).filter(Boolean));
    return ['ALL', ...[...s].sort()];
  }, [available]);

  const rows = useMemo(() => {
    const list = available || [];
    // Filter AFTER ranking so the rank always reflects the true queue position,
    // not the position within a filtered view.
    const ranked = list.map((s, i) => ({ ...s, fifoRank: i + 1 }));
    return deptFilter === 'ALL' ? ranked : ranked.filter(s => s.DepartmentCode === deptFilter);
  }, [available, deptFilter]);

  async function doRequest() {
    setBusy(true);
    setError(null);
    try {
      await requestPoolStaff({ staffId: requesting.StaffId, note });
      const name = requesting.Name;
      setRequesting(null);
      setNote('');
      onRequested?.(name);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="sched-pool-side-empty">Loading…</div>;

  if (!available || available.length === 0) {
    return (
      <div className="sched-pool-side-empty">
        Nobody is checked in as available right now.
      </div>
    );
  }

  return (
    <>
      {depts.length > 2 && (
        <div className="sched-pool-avail-filters">
          {depts.map(d => (
            <button
              key={d}
              type="button"
              className={`sched-mgr-pill${deptFilter === d ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setDeptFilter(d)}
            >
              {d === 'ALL' ? 'All' : d}
            </button>
          ))}
        </div>
      )}

      <div className="sched-pool-avail-list">
        {rows.map(s => (
          <div key={s.StaffId} className="sched-pool-avail-row">
            <span className={`sched-pool-assign-rank${s.fifoRank === 1 ? ' sched-pool-assign-rank--first' : ''}`}>
              {s.fifoRank}
            </span>
            <div className="sched-pool-avail-info">
              <div className="sched-pool-avail-name">{s.Name}</div>
              <div className="sched-pool-avail-sub">
                {s.Level}{s.DepartmentCode ? ` · ${s.DepartmentCode}` : ''}
                <span className="sched-pool-assign-free"> · free {fmtWaited(s.CheckedInAt)}</span>
              </div>
              {Array.isArray(s.pools) && s.pools.length > 0 && (
                <div className="sched-pool-avail-pools">
                  {s.pools.map(p => (
                    <span key={p} className="sched-pool-chip sched-pool-chip--sm" style={{ background: POOL_META[p]?.color }}>
                      {POOL_META[p]?.label || p}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="sched-pool-avail-right">
              {s.CapacityHours != null && (
                <span className="sched-pool-assign-cap">{Math.round(Number(s.CapacityHours) * 100) / 100}h</span>
              )}
              <button
                type="button"
                className="sched-mgr-modal-btn sched-pool-avail-req"
                onClick={() => { setRequesting(s); setNote(''); setError(null); }}
              >
                Request
              </button>
            </div>
          </div>
        ))}
      </div>

      {requesting && (
        <div className="sched-mgr-modal-backdrop" onClick={busy ? undefined : () => setRequesting(null)}>
          <div className="sched-mgr-modal" onClick={e => e.stopPropagation()}>
            <div className="sched-mgr-modal-header">
              <h3>Request {requesting.Name}</h3>
              {!busy && <button className="sched-mgr-sidebar-close" onClick={() => setRequesting(null)}>&times;</button>}
            </div>

            {/* Item 4.8 — say up front what this is FOR. Request came from the POC
                and is easy to mistake for "assign this person a job", which is a
                different flow entirely. Naming the alternative first, with a
                deeplink, means nobody has to guess which one they wanted. */}
            <div className="sched-pool-req-purpose">
              Use this when you have <strong>unspecified</strong> work — a project you
              could use help on that isn&rsquo;t a job in Practice Engine yet.
              <div className="sched-pool-req-purpose-alt">
                Want them on a <strong>specific job</strong> instead? Go to{' '}
                <Link to="/schedule/dashboard?tab=myjobs">My Jobs → Send to Pool</Link>
                {' '}— from there you can assign it to someone directly, or leave it for
                available staff to claim.
              </div>
            </div>

            <div className="sched-mgr-modal-job">
              <span className="sched-mgr-modal-job-name">{requesting.Name}</span>
              <span className="sched-mgr-modal-job-client">
                {requesting.Level}{requesting.DepartmentCode ? ` · ${requesting.DepartmentCode}` : ''}
              </span>
              <span className="sched-mgr-modal-job-budget">
                Free for {fmtWaited(requesting.CheckedInAt)}
                {requesting.CapacityHours != null ? ` · says they have ${Math.round(Number(requesting.CapacityHours) * 100) / 100}h` : ''}
              </span>
            </div>

            <div className="sched-mgr-modal-field">
              <label className="sched-mgr-modal-label">Message (optional)</label>
              <textarea
                className="sched-pool-notes"
                rows={3}
                maxLength={300}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="What you'd like them to pick up"
                disabled={busy}
              />
            </div>

            <div className="sched-pool-confirm-what">
              <div className="sched-pool-confirm-head">What this does</div>
              <ul>
                <li>
                  {requesting.Name} gets an email and an RPRS notification that you&rsquo;re asking,
                  with your message. You&rsquo;re copied on the email, so they can just reply.
                </li>
                <li>No job is assigned and nothing is written to PE</li>
                {/* Availability IS cleared now: they've been pointed at something, so
                    they're no longer idle and shouldn't hold queue position ahead of
                    people still genuinely waiting. */}
                <li>
                  <strong>Their &ldquo;available&rdquo; flag is cleared</strong> — they&rsquo;ve been
                  given a direction, so they come off the available list
                </li>
              </ul>
              <div className="sched-pool-confirm-note">
                To hand over an actual job, use <strong>Assign</strong> on that job&rsquo;s
                card in the pool instead.
              </div>
            </div>

            {error && <div className="sched-pool-error">{error}</div>}

            <div className="sched-pool-actions">
              <button type="button" className="sched-mgr-modal-btn sched-pool-btn-cancel"
                onClick={() => setRequesting(null)} disabled={busy}>Cancel</button>
              <button type="button" className="sched-mgr-modal-btn sched-pool-btn-confirm"
                onClick={doRequest} disabled={busy}>
                {busy ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
